import React from 'react';

import { Badge, Callout, Step } from './shared';

const ConnectTrello: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-4">Connect Trello</h2>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      Connecting Trello lets MFlow sync your boards for dashboard metrics, risk flags, and an AI assistant that can read
      and manage cards on your behalf. Unlike Jira, there&apos;s no project-selection step — all boards you have access
      to sync automatically.
    </p>

    <h3 className="font-semibold mb-3">Step-by-step</h3>
    <div className="space-y-3 mb-6">
      <Step n={1}>
        Open <strong>Integrations</strong> from the sidebar (plug icon) or navigate to{' '}
        <code className="bg-base-200 px-1 rounded text-xs">/integrations</code>.
      </Step>
      <Step n={2}>
        Find the <strong>Trello</strong> card and click <strong>Connect</strong>. A popup window opens Trello&apos;s
        OAuth consent screen.
      </Step>
      <Step n={3}>
        Log in to Trello if prompted, then approve access. The popup closes automatically once authorization succeeds.
      </Step>
      <Step n={4}>
        The Trello card updates to <Badge color="badge-success">Connected</Badge> and your boards start syncing right
        away — no further setup needed.
      </Step>
      <Step n={5}>
        Click <strong>Sync now</strong> any time you want fresh stats — for example right after making changes directly
        in Trello.
      </Step>
    </div>

    <Callout type="info">
      By default MFlow only <strong>reads</strong> from Trello. The AI agent can also create, edit, and move cards — but
      only when you explicitly ask it to.
    </Callout>

    <h3 className="font-semibold mt-5 mb-3">Permissions requested</h3>
    <ul className="text-sm text-base-content/70 space-y-1 list-disc list-inside mb-4">
      <li>Read boards, lists, and cards</li>
      <li>Write cards, comments, and labels</li>
      <li>Manage boards and lists</li>
    </ul>

    <Callout type="tip">
      Trello has no fixed status taxonomy — &quot;done&quot; is inferred positionally (the last active list on a board).
      Sprint-relative and flow-efficiency metrics aren&apos;t computable for Trello boards, but cycle time, reopen rate,
      and risk flags all work normally. See <strong>Jira, Trello &amp; Standalone</strong> for the full comparison.
    </Callout>

    <h3 className="font-semibold mt-5 mb-3">Disconnecting Trello</h3>
    <p className="text-sm text-base-content/70 leading-relaxed">
      Click <strong>Disconnect</strong> on the Trello card. All synced board data and tokens are removed from
      MFlow&apos;s servers. Your Trello workspace is not affected.
    </p>
  </div>
);

export default ConnectTrello;
